import { useState } from 'react';
import { Box, TablePagination, Typography } from '@mui/material';
import { CampaignsList as CampaignsListStyle } from '../styles';
import { ListDataTableCss } from '../../keywordResearch/keywordsLists/KeywordListsCss';
import { CitationListItem } from './CitationListItem';
import { useNavigate } from 'react-router-dom';
import { useGetCitations } from '../hooks';
import { StripeLoader } from 'src/common/StripeLoader';
import { RootState } from 'src/redux/store';
import { useSelector } from 'react-redux';
import { formatTimestamp } from 'src/common/config/FunctionHelper';
import { Card } from 'src/components/UI/Card';
import { useCitation, useClearCitationStateOnMount } from '../Providers/CitationProvider';

export const CampaignsList = () => {
  useClearCitationStateOnMount()
  const { campaignList, isFetching } = useGetCitations();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const { setCampaignId, setBLLocationId } = useCitation();

  let userDetails: any = useSelector(
    (state: RootState) => state.counter.userDetails
  );

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const startIndex = page * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const paginatedItems = campaignList?.slice(startIndex, endIndex) || [];

  const navigate = useNavigate();

  if (isFetching) {
    return <StripeLoader />;
  }

  if (campaignList?.length === 0) {
    return (
      <Card
        sx={{
          marginTop: '24px',
          marginBottom: '24px'
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2
          }}
        >
          <Typography sx={{ textAlign: 'center', width: '100%' }}>
            No campaigns found
          </Typography>
        </Box>
      </Card>
    );
  }

  return (
    <Box sx={CampaignsListStyle.container}>
      <Typography sx={CampaignsListStyle.title}>All Campaigns</Typography>

      <Box sx={CampaignsListStyle.listContainer}>
        {paginatedItems.map((campaign) => {
          const formattedDate = formatTimestamp(
            campaign.created_at,
            userDetails?.date_format || 'DD/MM/YYYY'
          );
          return (
            <CitationListItem
              key={campaign._id}
              title={campaign.businessInfo.name}
              status={campaign.status}
              date={formattedDate}
              onViewDetails={() => {
                if(campaign.status === "processing") {
                  setCampaignId(String(campaign.campaign_id));
                  setBLLocationId(String(campaign.location_id));
                  navigate('/local-seo/citations/scanning', {
                    state: { campaignId: campaign.campaign_id, locationId: campaign.location_id }
                  });
                  return;
                }

                if(campaign.status === "complete" && campaign.payment_status === "completed") {
                  setCampaignId(String(campaign.campaign_id));
                  setBLLocationId(String(campaign.location_id));
                  navigate('/local-seo/citations/campaign-in-progress', {
                    state: { campaignId: campaign.campaign_id, locationId: campaign.location_id }
                  });
                  return;
                }

                if(campaign.status === "on_hold" && campaign.payment_status === "completed") {
                  setCampaignId(String(campaign.campaign_id));
                  setBLLocationId(String(campaign.location_id));
                  setTimeout(() => {
                    navigate('/local-seo/citations/campaign-in-progress', {
                      state: { campaignId: campaign.campaign_id, locationId: campaign.location_id }
                    });
                  }, 0);
                  return;
                }

                if(campaign.status === "saved" && campaign.payment_status !== "completed") {
                  setCampaignId(String(campaign.campaign_id));
                  setBLLocationId(String(campaign.location_id));
                  setTimeout(() => {
                    navigate('/local-seo/citations/create-campaign', {
                      state: { campaignId: campaign.campaign_id, locationId: campaign.location_id }
                    });
                  }, 0);
                  return;
                }

                if(campaign.status === "saved" && campaign.payment_status === "completed") {
                  setCampaignId(String(campaign.campaign_id));
                  setBLLocationId(String(campaign.location_id));
                  setTimeout(() => {
                    navigate('/local-seo/citations/campaign-in-progress', {
                      state: { campaignId: campaign.campaign_id, locationId: campaign.location_id }
                    });
                  }, 0);
                  return;
                }

              }}
            />
          );
        })}
      </Box>

      {campaignList.length > 10 && (
        <Box sx={CampaignsListStyle.paginationContainer}>
          <TablePagination
            sx={ListDataTableCss?.pagination}
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={campaignList.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            SelectProps={{
              MenuProps: {
                disableScrollLock: true
              }
            }}
          />
        </Box>
      )}
    </Box>
  );
};
